import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { typography, zIndex } from "../styles/designSystem";

/**
 * Profile load sequence.
 *
 * The site's entry animation: a ROBCO terminal locating, decrypting, and mounting
 * Nicholas's personnel record, then handing over the screen. It replaces the older
 * scroll-driven "door" intro — this one is time-driven, so a visitor sees it without
 * having to discover that scrolling does something.
 *
 * Three rules keep it from being an obstacle:
 *   - it plays once per browser session (sessionStorage), not on every navigation
 *   - any key, click, or the Skip button ends it immediately
 *   - prefers-reduced-motion skips it entirely, on the first render
 */

const SESSION_KEY = "nl_profile_booted";

const TASKS = [
    "Searching personnel database",
    "Decrypting record",
    "Mounting employment history",
    "Loading project archive",
    "Verifying clearance",
];

// Steps past the task list: identity block, then the access banner.
const STEP_IDENTITY = TASKS.length;
const STEP_GRANTED = TASKS.length + 1;
const TOTAL_STEPS = TASKS.length + 2;

const TICK_MS = 230;
const HOLD_MS = 520;
// The exit is staged: console text clears first, then the opaque screen behind it.
// Fading both together would cross-dissolve the terminal with the hero underneath
// and leave both unreadable for a moment.
const CONTENT_FADE_MS = 170;
const SCREEN_FADE_MS = 250;
const FADE_MS = CONTENT_FADE_MS + SCREEN_FADE_MS;

const BAR_CELLS = 24;

export const hasBootedThisSession = () => {
    try {
        return window.sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
        // Private browsing or blocked storage — treat as "not yet booted".
        return false;
    }
};

const markBooted = () => {
    try {
        window.sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
        /* non-fatal: the sequence just plays again next navigation */
    }
};

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ===== Animations =====
const blink = keyframes`
    0%, 49%   { opacity: 1; }
    50%, 100% { opacity: 0; }
`;

const typeIn = keyframes`
    from { opacity: 0; transform: translateY(2px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Screen = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${zIndex.crt - 1};
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 1.5rem;
    cursor: pointer;
    opacity: ${({ $leaving }) => ($leaving ? 0 : 1)};
    transition: opacity ${SCREEN_FADE_MS}ms ease-out ${CONTENT_FADE_MS}ms;
`;

const Console = styled.div`
    width: 100%;
    max-width: 620px;
    opacity: ${({ $leaving }) => ($leaving ? 0 : 1)};
    transition: opacity ${CONTENT_FADE_MS}ms ease-out;
    font-family: ${typography.fontFamily.display};
    font-size: 0.85rem;
    letter-spacing: ${typography.letterSpacing.wide};
    text-transform: uppercase;
    text-shadow: 0 0 8px ${({ theme }) => theme.glow};

    @media (max-width: 768px) {
        font-size: 0.68rem;
        letter-spacing: 0.06em;
    }
`;

const Plate = styled.div`
    color: ${({ theme }) => theme.subtleText};
    text-shadow: none;
    line-height: 1.7;
    margin-bottom: 1.75rem;
`;

const Command = styled.div`
    margin-bottom: 1.5rem;
`;

// One "TASK ......... OK" line. The dots are a flexing rule so every OK lines up
// on the right regardless of label length.
const TaskList = styled.div`
    min-height: 9em;
`;

const Task = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    min-height: 1.8em;
    ${css`animation: ${typeIn} 0.14s steps(2, end) both;`}
`;

const Dots = styled.span`
    flex: 1;
    border-bottom: 1px dotted ${({ theme }) => theme.borderMedium};
    transform: translateY(-0.25rem);
`;

const Ok = styled.span`
    color: ${({ theme }) => theme.primary};
`;

const Bar = styled.div`
    margin: 1.5rem 0 0.5rem;
    color: ${({ theme }) => theme.primary};
    letter-spacing: 0;
    white-space: nowrap;
    overflow: hidden;
`;

const Percent = styled.div`
    color: ${({ theme }) => theme.subtleText};
    text-shadow: none;
`;

// Holds the identity block and access banner, sized for both from the start.
const Result = styled.div`
    min-height: 13em;
`;

const Identity = styled.div`
    margin-top: 1.75rem;
    padding-top: 1.25rem;
    border-top: 1px solid ${({ theme }) => theme.borderMedium};
    ${css`animation: ${typeIn} 0.2s steps(2, end) both;`}
`;

const Field = styled.div`
    display: flex;
    gap: 0.75rem;
    margin-bottom: 0.4rem;

    span:first-child {
        color: ${({ theme }) => theme.subtleText};
        text-shadow: none;
        min-width: 8.5rem;
    }

    span:last-child {
        color: ${({ theme }) => theme.text};
    }
`;

const Granted = styled.div`
    margin-top: 1.5rem;
    color: ${({ theme }) => theme.background};
    background: ${({ theme }) => theme.primary};
    display: inline-block;
    padding: 0.25rem 0.7rem;
    text-shadow: none;
    ${css`animation: ${typeIn} 0.14s steps(2, end) both;`}
`;

const Caret = styled.span`
    display: inline-block;
    margin-left: 0.35rem;
    ${css`animation: ${blink} 1.1s steps(1, end) infinite;`}
`;

const Skip = styled.button`
    position: absolute;
    opacity: ${({ $leaving }) => ($leaving ? 0 : 1)};
    transition: opacity ${CONTENT_FADE_MS}ms ease-out;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    background: none;
    border: 1px solid ${({ theme }) => theme.borderMedium};
    border-radius: 0;
    padding: 0.4rem 0.9rem;
    cursor: pointer;
    font-family: ${typography.fontFamily.display};
    font-size: 0.62rem;
    letter-spacing: ${typography.letterSpacing.widest};
    text-transform: uppercase;
    color: ${({ theme }) => theme.subtleText};

    &:hover {
        color: ${({ theme }) => theme.primary};
        border-color: ${({ theme }) => theme.primary};
    }
`;

const ProfileBoot = ({ onComplete }) => {
    const [step, setStep] = useState(0);
    const [leaving, setLeaving] = useState(false);
    const doneRef = useRef(false);

    const finish = useCallback(() => {
        if (doneRef.current) return;
        doneRef.current = true;
        markBooted();
        setLeaving(true);
        window.setTimeout(onComplete, FADE_MS);
    }, [onComplete]);

    // Advance the sequence, then hold on "access granted" before handing over.
    useEffect(() => {
        if (step >= TOTAL_STEPS) {
            const t = window.setTimeout(finish, HOLD_MS);
            return () => window.clearTimeout(t);
        }
        const t = window.setTimeout(() => setStep((s) => s + 1), TICK_MS);
        return () => window.clearTimeout(t);
    }, [step, finish]);

    // Any key skips. Click is handled on the overlay itself.
    useEffect(() => {
        const onKey = () => finish();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [finish]);

    // The page behind must not scroll while the terminal owns the screen.
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, []);

    const doneTasks = Math.min(step, TASKS.length);
    const pct = Math.round((doneTasks / TASKS.length) * 100);
    const filled = Math.round((doneTasks / TASKS.length) * BAR_CELLS);

    return (
        <Screen $leaving={leaving} onClick={finish} role="status" aria-label="Loading profile">
            <Console $leaving={leaving}>
                <Plate>
                    ROBCO Industries (TM) Termlink Protocol
                    <br />
                    Copyright 2075&ndash;2077 ROBCO Industries
                    <br />
                    &ndash;Server 1&ndash;
                </Plate>

                <Command>
                    &gt; Load User Profile
                    {step < TOTAL_STEPS && <Caret>&#9608;</Caret>}
                </Command>

                <TaskList>
                    {TASKS.slice(0, doneTasks).map((task) => (
                        <Task key={task}>
                            <span>{task}</span>
                            <Dots aria-hidden="true" />
                            <Ok>OK</Ok>
                        </Task>
                    ))}
                </TaskList>

                <Bar aria-hidden="true">
                    [{"█".repeat(filled)}
                    {"░".repeat(BAR_CELLS - filled)}]
                </Bar>
                <Percent>{pct}% Complete</Percent>

                <Result>
                    {step >= STEP_IDENTITY && (
                        <Identity>
                            <Field>
                                <span>User</span>
                                <span>Nicholas Lovett</span>
                            </Field>
                            <Field>
                                <span>Clearance</span>
                                <span>Senior Software Engineer</span>
                            </Field>
                            <Field>
                                <span>Status</span>
                                <span>Available for hire</span>
                            </Field>
                        </Identity>
                    )}

                    {step >= STEP_GRANTED && <Granted>&gt; Access Granted</Granted>}
                </Result>
            </Console>

            <Skip
                $leaving={leaving}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    finish();
                }}
            >
                Skip &#9654;
            </Skip>
        </Screen>
    );
};

export { prefersReducedMotion };
export default ProfileBoot;
